import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Dot,
  MapPin,
  Quote,
  Sparkles,
} from "lucide-react";
import { STORIES, STORY_FILTERS } from "../../data/stories";
import { Reveal, SectionHeading } from "../common";

const ACCENT = {
  Wildlife: "bg-amber-100 text-amber-800",
  Animals: "bg-sky-100 text-sky-800",
  Conservation: "bg-ivi-mint text-ivi-greenDark",
  Teaching: "bg-violet-100 text-violet-800",
  Community: "bg-rose-100 text-rose-800",
};

export default function Stories() {
  const [filter, setFilter] = useState("All stories");
  const [activeId, setActiveId] = useState(STORIES[0].id);
  const [shot, setShot] = useState(0);

  const list = useMemo(
    () => (filter === "All stories" ? STORIES : STORIES.filter((s) => s.accent === filter)),
    [filter]
  );

  const active = list.find((s) => s.id === activeId) || list[0] || STORIES[0];
  const images = [active.cover, ...active.gallery];
  const current = images[Math.min(shot, images.length - 1)];
  const index = list.findIndex((s) => s.id === active.id);

  const pick = (id) => {
    setActiveId(id);
    setShot(0);
  };

  const step = (dir) => {
    const next = (index + dir + list.length) % list.length;
    pick(list[next].id);
  };

  const choose = (f) => {
    setFilter(f);
    const first = f === "All stories" ? STORIES[0] : STORIES.find((s) => s.accent === f);
    if (first) pick(first.id);
  };

  return (
    <section id="stories" className="relative overflow-hidden bg-white py-24 lg:py-32">
      <div className="pointer-events-none absolute -left-40 top-32 h-96 w-96 rounded-full bg-ivi-mint/60 blur-3xl" />
      <div className="pointer-events-none absolute -right-40 bottom-32 h-96 w-96 rounded-full bg-ivi-sand blur-3xl" />

      <div className="relative mx-auto max-w-[1400px] px-5 lg:px-8">
        <SectionHeading
          eyebrow="Volunteer stories"
          title="Twelve journeys, told in their own words"
          text="Stray dogs in Kathmandu, elephants on Koh Samui, cheetah kittens in Cape Town, turtles in Nusa Penida. Real people, real projects, real photos."
        />

        {/* Filters */}
        <Reveal className="mt-10 flex flex-wrap justify-center gap-2">
          <div className="flex flex-wrap justify-center gap-2">
            {STORY_FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => choose(f)}
                className={`rounded-full px-5 py-2.5 text-[14px] font-semibold transition-[background-color,color,border-color,transform] duration-200 hover:-translate-y-0.5 ${
                  filter === f
                    ? "bg-ivi-green text-white shadow-[0_10px_22px_-12px_rgba(90,158,47,1)]"
                    : "border border-ivi-mintDeep bg-white text-ivi-slate hover:border-ivi-green"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </Reveal>

        {/* Feature panel */}
        <Reveal className="mt-12">
          <div className="grid overflow-hidden rounded-[34px] bg-ivi-sand shadow-[0_34px_80px_-48px_rgba(24,40,32,0.6)] lg:grid-cols-[1.05fr_1fr]">
            {/* Visual */}
            <div className="relative p-6 sm:p-8 lg:p-10">
              <div className="relative overflow-hidden rounded-[26px] bg-ivi-slate">
                <img
                  key={current}
                  src={current}
                  alt={`${active.kicker} ${active.place}`}
                  className="h-[340px] w-full object-cover sm:h-[430px] lg:h-[500px]"
                  style={{ animation: "heroUp 0.65s cubic-bezier(0.22,1,0.36,1) both" }}
                />
                <span className="absolute inset-0 bg-gradient-to-t from-ivi-slate/80 via-transparent to-transparent" />
                <span
                  className={`absolute left-5 top-5 rounded-full px-3.5 py-1.5 font-heading text-[11px] font-extrabold uppercase tracking-[0.14em] ${
                    ACCENT[active.accent] || "bg-ivi-mint text-ivi-greenDark"
                  }`}
                >
                  {active.accent}
                </span>
                <span className="absolute inset-x-6 bottom-6">
                  <span className="block font-heading text-[24px] font-extrabold leading-tight text-white sm:text-[28px]">
                    {active.volunteer}
                  </span>
                  <span className="mt-1 flex items-center gap-1.5 text-[13px] font-semibold text-white/80">
                    <MapPin className="h-3.5 w-3.5" /> {active.place}
                    <Dot className="h-4 w-4" /> {active.date}
                  </span>
                </span>
              </div>

              {images.length > 1 && (
                <div className="mt-4 flex gap-3">
                  {images.map((im, i) => (
                    <button
                      key={im}
                      type="button"
                      onClick={() => setShot(i)}
                      aria-label={`Photo ${i + 1}`}
                      className={`h-16 w-20 overflow-hidden rounded-xl ring-2 transition-[ring-color,transform] duration-200 hover:-translate-y-0.5 ${
                        i === Math.min(shot, images.length - 1)
                          ? "ring-ivi-green"
                          : "ring-transparent"
                      }`}
                    >
                      <img src={im} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Copy */}
            <div className="flex flex-col justify-center p-8 sm:p-10 lg:py-14 lg:pr-14">
              <p className="font-heading text-[13px] font-bold uppercase tracking-[0.18em] text-ivi-greenDark">
                {active.kicker}
              </p>
              <h3 className="mt-3 font-heading text-[30px] font-extrabold leading-tight tracking-[-0.02em] text-ivi-slate sm:text-[38px]">
                {active.program}
              </h3>

              <blockquote className="relative mt-7">
                <Quote className="absolute -left-1 -top-2 h-8 w-8 text-ivi-mintDeep" />
                <p className="relative pl-9 font-heading text-[19px] font-bold leading-relaxed text-ivi-slate sm:text-[21px]">
                  {active.lead}
                </p>
              </blockquote>

              <div className="mt-6 space-y-4">
                {active.body.map((p, i) => (
                  <p key={i} className="text-[16px] leading-relaxed text-ivi-slateSoft">
                    {p}
                  </p>
                ))}
              </div>

              {active.facts.length > 0 && (
                <div className="mt-7 rounded-2xl bg-white p-6">
                  <p className="flex items-center gap-2 font-heading text-[12px] font-bold uppercase tracking-[0.16em] text-ivi-greenDark">
                    <Sparkles className="h-3.5 w-3.5" /> Why we need you in {active.place}
                  </p>
                  <ul className="mt-4 space-y-2.5">
                    {active.facts.map((f) => (
                      <li key={f} className="flex gap-3 text-[14px] leading-relaxed text-ivi-slate">
                        <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-ivi-green" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to={active.source}
                  className="group inline-flex items-center gap-2 rounded-full bg-ivi-green px-7 py-3.5 font-heading text-[15px] font-bold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-ivi-greenDark"
                >
                  Read the full story
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:rotate-45" />
                </Link>
                <span className="flex items-center gap-2">
                  <button
                    type="button"
                    aria-label="Previous story"
                    onClick={() => step(-1)}
                    className="grid h-11 w-11 place-items-center rounded-full border border-ivi-mintDeep bg-white text-ivi-slate transition-colors duration-200 hover:bg-ivi-mint"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    aria-label="Next story"
                    onClick={() => step(1)}
                    className="grid h-11 w-11 place-items-center rounded-full border border-ivi-mintDeep bg-white text-ivi-slate transition-colors duration-200 hover:bg-ivi-mint"
                  >
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </span>
                <span className="font-heading text-[13px] font-bold text-ivi-slateSoft">
                  {index + 1} / {list.length}
                </span>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Story rail */}
        <div className="mt-10">
          <p className="font-heading text-[12px] font-bold uppercase tracking-[0.16em] text-ivi-slateSoft">
            All {list.length} stories tap to read
          </p>
          <div className="no-scrollbar mt-4 flex gap-4 overflow-x-auto pb-3">
            {list.map((s) => {
              const on = s.id === active.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => pick(s.id)}
                  className={`group relative w-[230px] shrink-0 overflow-hidden rounded-3xl text-left transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 ${
                    on
                      ? "ring-2 ring-ivi-green shadow-[0_20px_44px_-28px_rgba(24,40,32,0.55)]"
                      : "ring-1 ring-ivi-mintDeep"
                  }`}
                >
                  <span className="relative block h-36 overflow-hidden">
                    <img
                      src={s.cover}
                      alt={s.kicker}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-110"
                    />
                    <span className="absolute inset-0 bg-gradient-to-t from-ivi-slate/75 to-transparent" />
                    <span className="absolute inset-x-4 bottom-3 block font-heading text-[15px] font-extrabold leading-tight text-white">
                      {s.volunteer}
                    </span>
                  </span>
                  <span className="block bg-white p-4">
                    <span className="block font-heading text-[13px] font-bold leading-snug text-ivi-slate">
                      {s.kicker}
                    </span>
                    <span className="mt-1 flex items-center gap-1.5 text-[12px] font-semibold text-ivi-slateSoft">
                      <MapPin className="h-3 w-3 text-ivi-green" /> {s.place}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
